import { defineStore } from 'pinia'

import {
  obterFuncionalidade as consultarFuncionalidade,
  temFuncionalidade as consultarTemFuncionalidade,
  temModulo as consultarTemModulo,
  temPermissao as consultarTemPermissao,
  usuarioEhColaborador as consultarUsuarioEhColaborador,
  usuarioEhGestor as consultarUsuarioEhGestor,
  usuarioEhRh as consultarUsuarioEhRh,
  usuarioTemEscopo as consultarUsuarioTemEscopo,
} from './acesso.consultas'
import { normalizarContextoAcesso } from './acesso.normalizador'
import { normalizarDisponibilidade } from './disponibilidade.normalizador'
import type {
  ConsultaPermissao,
  ContextoAcesso,
  ContextoAcessoCarregado,
  EstadoCarregamento,
  PerfilAcessoAtivo,
  SnapshotDisponibilidade,
  SnapshotDisponibilidadeBruto,
  TipoUsuario,
} from './acesso.tipos'

export const normalizarPerfilAtivo = (perfil: unknown): PerfilAcessoAtivo => (
  perfil === 'pessoal' ? 'pessoal' : 'gestor'
)

const criarEstadoInicial = (): ContextoAcesso => ({
  statusUsuario: 'inicial',
  statusDisponibilidade: 'inicial',
  usuario: null,
  empresa: null,
  perfilAtivo: 'gestor',
  permissoes: [],
  disponibilidade: {
    modulos: {},
    funcionalidades: {},
    funcionalidadesLegadas: {},
    origensFuncionalidades: {},
  },
})

export const useAcessoStore = defineStore('acesso', {
  state: criarEstadoInicial,

  actions: {
    iniciarCarregamentoUsuario (): void {
      this.statusUsuario = 'carregando'
    },

    falharCarregamentoUsuario (): void {
      this.statusUsuario = 'erro'
    },

    aplicarContextoUsuario (contexto: ContextoAcessoCarregado): void {
      this.usuario = contexto.usuario
      this.empresa = contexto.empresa
      this.permissoes = contexto.permissoes
      this.statusUsuario = 'pronto'
    },

    mapearContextoUsuario (payload: unknown): void {
      let contexto

      try {
        contexto = normalizarContextoAcesso(payload)
      } catch (erro) {
        this.falharCarregamentoUsuario()
        throw erro
      }

      const compMan = (payload as { compMan: Record<string, unknown> }).compMan
      this.aplicarContextoUsuario(contexto)
      this.$patch({
        disponibilidade: {
          funcionalidadesLegadas: {
            'banco-de-horas': { habilitada: compMan.hasWhbank === true },
            solicitacoes: { habilitada: compMan.hasApprovals === true },
          },
          origensFuncionalidades: {
            'banco-de-horas': 'legado',
            solicitacoes: 'legado',
          },
        },
      })
    },

    iniciarCarregamentoDisponibilidade (): void {
      this.statusDisponibilidade = 'carregando'
    },

    aplicarDisponibilidade (
      snapshot: SnapshotDisponibilidade,
      statusFinal: Extract<EstadoCarregamento, 'pronto' | 'erro'>,
    ): void {
      this.disponibilidade = snapshot
      this.statusDisponibilidade = statusFinal
    },

    mapearModulos (modulos: SnapshotDisponibilidadeBruto['modulos']): void {
      const disponibilidade = normalizarDisponibilidade({
        modulos,
        funcionalidades: {},
      })

      this.disponibilidade.modulos = disponibilidade.modulos
      this.statusDisponibilidade = 'pronto'
    },

    mapearFuncionalidades (
      funcionalidades: SnapshotDisponibilidadeBruto['funcionalidades'],
    ): void {
      const funcionalidadesLegadas = Object.fromEntries(
        Object.entries(this.disponibilidade.funcionalidadesLegadas).map(([slug, flag]) => [
          slug,
          flag.habilitada,
        ]),
      )

      const disponibilidade = normalizarDisponibilidade({
        modulos: {},
        funcionalidades,
        funcionalidadesLegadas,
      })

      this.disponibilidade.funcionalidades = disponibilidade.funcionalidades
      this.disponibilidade.origensFuncionalidades = disponibilidade.origensFuncionalidades
      this.statusDisponibilidade = 'pronto'
    },

    definirPerfilAtivo (perfil: unknown): void {
      this.perfilAtivo = normalizarPerfilAtivo(perfil)
    },

    resetar (): void {
      this.$reset()
    },

    temPermissao (consulta: ConsultaPermissao): boolean {
      return consultarTemPermissao(this, consulta)
    },

    usuarioTemEscopo (escopos: TipoUsuario | readonly TipoUsuario[]): boolean {
      return consultarUsuarioTemEscopo(this, escopos)
    },

    usuarioEhRh (): boolean {
      return consultarUsuarioEhRh(this)
    },

    usuarioEhGestor (): boolean {
      return consultarUsuarioEhGestor(this)
    },

    usuarioEhColaborador (): boolean {
      return consultarUsuarioEhColaborador(this)
    },

    temModulo (slug: string): boolean | undefined {
      return consultarTemModulo(this, slug)
    },

    temFuncionalidade (slug: string): boolean | undefined {
      return consultarTemFuncionalidade(this, slug)
    },

    obterFuncionalidade (slug: string) {
      return consultarFuncionalidade(this, slug)
    },
  },
})
